/**
 * auth Zustand store — 当前登录态 + access/refresh token
 *
 * 持久化策略：access + refresh + user 一并存 localStorage（admin 后台运营员设备相对可信），
 * localStorage 是同浏览器多 tab 共享会话的唯一事实源（跨 tab 同步见 ./auth-cross-tab-sync）。
 *
 * Why access 也落盘：access 绑定 sid，refresh 每轮换一次旧 sid 即被撤销、其 access 立刻失效。
 * access 只在单 tab 内存时，任一 tab 续期都会作废其他 tab 的 access → 它们再续期又反过来作废这个 tab，
 * 多 tab 间互相踢。access 有效期 15min、权限严格弱于已落盘的 refresh token，同置不扩大暴露面。
 *
 * 注意：与 C 端 client 不同，admin 后台没有 Keychain；localStorage 是合理折中
 */

import type { AdminUserResponse } from '@novel-admin/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** localStorage key（zustand persist 存 `{ state: PersistedAuthSnapshot, version }`） */
export const AUTH_STORAGE_KEY = 'novel-admin-auth';

/** 落盘的会话快照 —— 多 tab 共享的唯一事实源 */
export type PersistedAuthSnapshot = {
  accessToken: string | null;
  refreshToken: string | null;
  user: AdminUserResponse | null;
};

/**
 * 解析 zustand persist 落盘值（`{ state, version }`）。
 * key 不存在 / 格式异常 → null：没有可采纳的信息（不把被清的存储当成登出）。
 * 旧版本只落 refresh + user，access 解析为 null。
 */
export function parsePersistedAuthSnapshot(raw: string | null): PersistedAuthSnapshot | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as { state?: Partial<PersistedAuthSnapshot> } | null;
    const state = parsed?.state;
    if (!state || typeof state !== 'object') return null;
    return {
      accessToken: typeof state.accessToken === 'string' ? state.accessToken : null,
      refreshToken: typeof state.refreshToken === 'string' ? state.refreshToken : null,
      user: state.user ?? null,
    };
  } catch {
    return null;
  }
}

/** 读 localStorage 当前的共享会话快照；不可用 / 读不出时返回 null。 */
export function readPersistedAuthSnapshot(): PersistedAuthSnapshot | null {
  try {
    return parsePersistedAuthSnapshot(window.localStorage.getItem(AUTH_STORAGE_KEY));
  } catch {
    return null;
  }
}

let persistWritesSuppressed = false;

/**
 * 执行 `apply` 期间的 store 变更只进内存、不回写 localStorage。
 * 供采纳他 tab 会话用：回写的是刚读到的旧值，跨进程 localStorage 最终一致，
 * 回写可能晚于源 tab 的下一次写入落地，把共享会话覆盖回旧 token。
 */
export function applyWithoutPersisting(apply: () => void): void {
  persistWritesSuppressed = true;
  try {
    apply();
  } finally {
    persistWritesSuppressed = false;
  }
}

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: AdminUserResponse | null;
  /**
   * 会话代际——**clearSession（会话拆除）与 setSession 建立新登录会话时自增**，仅 refresh token
   * 同会话轮换（postRefresh 传 keepEpoch）保持不变。用于 authedFetch 区分「同会话内 token 轮换」
   * (可安全重试) 与「登出/换号/重登」(旧请求不得 replay、旧 401 不得登出新会话)。仅内存（不持久化；
   * 整页刷新天然重置为 0；他 tab 换号 / 登出同步进来时由 auth-cross-tab-sync 自增）。
   */
  sessionEpoch: number;

  /**
   * 写入登录态。`opts.keepEpoch=true` 仅供 refresh token 同会话轮换用（保持当前 epoch）；
   * 缺省（登录页建立新会话）自增 epoch，使旧会话在途请求据此拒绝 replay（默认自增 = 保守安全侧）。
   */
  setSession: (
    data: { access: string; refresh: string; user: AdminUserResponse },
    opts?: { keepEpoch?: boolean },
  ) => void;
  setUser: (user: AdminUserResponse) => void;
  clearSession: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      sessionEpoch: 0,

      setSession: (data, opts) =>
        set((s) => ({
          accessToken: data.access,
          refreshToken: data.refresh,
          user: data.user,
          // 默认视为「建立新登录会话」→ 自增 epoch；refresh 同会话轮换由 postRefresh 传 keepEpoch 保持
          sessionEpoch: opts?.keepEpoch ? s.sessionEpoch : s.sessionEpoch + 1,
        })),
      setUser: (user) => {
        const current = get();
        // 只接受本会话账号的资料：组件卸载后仍会执行的 mutation onSuccess 可能带着他 tab 换号前的账号
        if (current.user?.id !== user.id) return;
        // persist 会把整份快照（含 token）落盘：共享区已被他 tab 改写（本 tab 内存滞后、storage 事件未送达）时，
        // 同账号续期 → 连同共享区最新 token 一起落盘，不把作废的 token 盖回去；已登出 / 换号 → 资料所属会话已不在，不写
        const shared = readPersistedAuthSnapshot();
        if (
          shared &&
          (shared.refreshToken !== current.refreshToken ||
            shared.accessToken !== current.accessToken)
        ) {
          if (!shared.refreshToken || shared.user?.id !== user.id) return;
          set({
            user,
            accessToken: shared.accessToken ?? current.accessToken,
            refreshToken: shared.refreshToken,
          });
          return;
        }
        set({ user });
      },
      clearSession: () =>
        set((s) => ({
          accessToken: null,
          refreshToken: null,
          user: null,
          sessionEpoch: s.sessionEpoch + 1,
        })),
    }),
    {
      name: AUTH_STORAGE_KEY,
      // getStorage 内取 localStorage：被禁用时抛错 → persist 退化为纯内存（与默认 storage 行为一致）
      storage: createJSONStorage(() => {
        const localStorageArea = window.localStorage;
        return {
          getItem: (name) => localStorageArea.getItem(name),
          setItem: (name, value) => {
            if (!persistWritesSuppressed) localStorageArea.setItem(name, value);
          },
          removeItem: (name) => localStorageArea.removeItem(name),
        };
      }),
      // sessionEpoch 仅内存（tab 内会话代际，跨 tab 无意义）；三项会话字段落盘供整页刷新与多 tab 共享
      partialize: (state): PersistedAuthSnapshot => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);
